import { apiClient } from "@api/client";
import { request } from "@api/request";
import { toDestinationE164 } from "@phone/dialNumber";
import type { PhoneCallTone } from "@phone/phoneCallOutcome";
import { isOutbound, type PhoneCall } from "@phone/phoneCallsClient";
import { formatPhoneNumber } from "@phone/phoneSetupClient";
import type { ChipTone } from "@scribenote/brian-design-system";
import { useQuery } from "@tanstack/react-query";
import { type Task, toTask } from "@tasks/tasksClient";

interface CallParty {
  outbound: boolean;
  name: string;
  /** Null when `name` is the number. */
  number: string | null;
  callbackE164: string | null;
}

export const callParty = (call: PhoneCall): CallParty => {
  const outbound = isOutbound(call);
  // Outbound `fromNumber` is the clinic's own caller ID; the other party is the recipient.
  const raw = outbound ? call.recipientE164 : call.fromNumber;
  const formatted = raw ? formatPhoneNumber(raw) : null;
  const named = outbound
    ? (call.clientName ?? call.callerName)
    : call.callerName;
  return {
    outbound,
    name: named ?? formatted ?? (outbound ? "Outbound call" : "Unknown caller"),
    number: named ? formatted : null,
    callbackE164: toDestinationE164(raw ?? ""),
  };
};

export const outcomeChipTone: Record<
  Exclude<PhoneCallTone, "neutral">,
  ChipTone
> = {
  working: "recorded",
  brian: "brian",
  flag: "attention",
  stopped: "declined",
};

const fetchTask = (id: string): Promise<Task> =>
  request(() =>
    apiClient.GET("/tasks/{task_uuid}", {
      params: { path: { task_uuid: id } },
    }),
  ).then(toTask);

/** Under `["tasks"]` so resolving the to-do anywhere refreshes it here. */
export const useCallTask = (taskId: string | null) =>
  useQuery({
    queryKey: ["tasks", "detail", taskId],
    queryFn: () => fetchTask(taskId!),
    enabled: taskId !== null,
  });
